import Image from "next/image";
import Link from "next/link";

import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import { Button } from "@/components/ui/button";

interface AppSectionContent {
  badge: string;
  heading: string;
  description: string;
  ctaLabel: string;
  backgroundImage: string;
}

interface AppSectionProps {
  content: AppSectionContent;
}

export default function AppSection({ content }: AppSectionProps) {
  const { calPath } = useCalendarLink();

  return (
    <section className="relative w-full h-[485px] md:h-[700px] lg:h-[972px] overflow-hidden">
      <div className="absolute inset-0 bg-ash-600">
        <Image src={content.backgroundImage} alt="Rebolt app in the field" fill className="object-cover" />
      </div>

      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <radialGradient
            id="app-section-vignette"
            gradientUnits="userSpaceOnUse"
            cx="0"
            cy="0"
            r="10"
            gradientTransform="matrix(-1.5 62.031 -110.28 -2.4027 864 486)"
          >
            <stop stopColor="rgba(23,23,23,0)" offset="0" />
            <stop stopColor="rgba(23,23,23,1)" offset="0.87938" />
          </radialGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#app-section-vignette)" />
      </svg>

      <div className="relative z-10 flex flex-col items-center md:items-start justify-center h-full px-5 py-12 md:px-16 md:py-16 lg:px-28 lg:py-16 max-w-screen-3xl mx-auto">
        <div className="flex flex-col gap-8 max-w-3xl text-center md:text-left">
          <div className="flex flex-col gap-4 md:gap-6">
            <div className="flex flex-col gap-3 md:gap-6">
              <span className="text-clay-300 text-xs font-bold uppercase tracking-[1px]">{content.badge}</span>
              <h2 className="font-clash font-medium text-4xl lg:text-6xl leading-none tracking-tight text-white">
                {content.heading}
              </h2>
            </div>
            <p className="text-sm md:text-base text-white leading-relaxed">{content.description}</p>
          </div>

          <div className="flex justify-center md:justify-start">
            <Button
              size="lg"
              data-cal-link={calPath}
              data-cal-config='{"layout":"month_view"}'
              className="h-12 rounded-full bg-white text-ash-700 font-semibold hover:bg-white/90 border border-ash-200 px-6"
            >
              {content.ctaLabel}
            </Button>
          </div>
        </div>
      </div>

      <div className="hidden md:flex items-center gap-4 absolute bottom-8 right-8 lg:bottom-16 lg:right-28 z-10">
        <Link href="https://rebolthq.com/ios" target="_blank" rel="noopener noreferrer">
          <Image
            src="/download-app-store.svg"
            alt="Download on the App Store"
            width={140}
            height={42}
            className="h-10 sm:h-12 w-auto"
          />
        </Link>
        <Link href="https://rebolthq.com/android" target="_blank" rel="noopener noreferrer">
          <Image
            src="/download-google-play.svg"
            alt="Get it on Google Play"
            width={140}
            height={42}
            className="h-10 sm:h-12 w-auto"
          />
        </Link>
      </div>
    </section>
  );
}
